import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table'
import { Dropdown } from 'react-bootstrap'
import { toastr } from 'react-redux-toastr'
import { connect } from 'react-redux'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'

import SearchBar from '../../component/dump/menuComponents/SearchBar'
import CustomButton from '../../component/CustomButton'
import CustomOverlayTrigger from '../../component/CustomOverlayTrigger'
import ModalErrorStatus from './ModalErrorStatus'
import ModalSilencedErrors from './ModalSilencedErrors'

import { tableAdjustableColOptions } from '../../component/helpers/table'
import { formatGroup } from '../../component/helpers/tablesFuncHelpers'
import { filterIcon } from '../../component/helpers/iconHelpers'
import { isInternalUser } from '../../component/helpers/constants'

import '../../component/dump/menuComponents/menuComponents.sass'
import './CameraHealth.sass'
import { deviceAcknowledgeClear, deviceSilenceErrors, getDeviceCameraHealthError } from "../../../../shared/api/action/DeviceActions";
import { getCompanyCameraHealthError } from "../../../../shared/api/action/CompaniesActions";

const DeviceHealth = (props) => {
	const navigate = useNavigate();
	const { t, i18n } = useTranslation('translation', { keyPrefix: 'manageVehiclesPage.cameraHealth' });
	const { user, company, webfleetDeviceId, toggleLoader } = props
	const userRole = user.roles[0]

	const [state, setState] = useState({
		searchText: '',
		alerts: [],
		initialAlerts: [],
		groups: [],
		statuses: [],
		sortName: 'vehicle_name',
		sortOrder: 'asc',
		selectedAlert: null,
		showModalErrorStatus: false,
		selectedDevice: null,
		showModalSilencedErrors: false,
	})
	const { searchText, alerts, initialAlerts, groups, statuses, sortName, sortOrder, selectedAlert, showModalErrorStatus, selectedDevice, showModalSilencedErrors } = state

	const [initialData, setInitialData] = useState([]);
	const isWebfleetMode = webfleetDeviceId.length > 0;

	useEffect(() => {
		if (company && company.company_id) {
			getCameraHealthAlerts()
		}
	}, [company.company_id])

	const getCameraHealthAlerts = () => {
		// gets devices alerts/errors for company or for device when is isWebfleetMode
		let funcName
		if (isWebfleetMode) {
			funcName = getDeviceCameraHealthError({ device_id: webfleetDeviceId })
		}
		else {
			const params = {
				company_id: company.company_id,
				include_data: 'group_error'
			}
			if (!isInternalUser(userRole)) {
				params.err_codes = 'SD20'
			}
			funcName = getCompanyCameraHealthError(params)
		}

		toggleLoader(true);
		funcName
			.then(res => {
				toggleLoader(false)
				const { device_errors } = res.data.response
				const resAlerts = Array.isArray(device_errors) ? device_errors : [device_errors] // webfleet request returns object instead of array
				const alerts = []
				resAlerts.forEach((item) => {
					item.status = item.status.filter(({ err_code }) => !['E001', 'E003'].includes(err_code))

					//For internal users we have to display all of error BUT for other users just SD20
					if (!isInternalUser(userRole)) {
						item.status = item.status.filter(({err_code}) => err_code === 'SD20')
					}

					if (item.status.length) {
						alerts.push(item)
					}
				})
				const initialGroups = []
				const errorCodes = []
				const errorTypes = []
				alerts.forEach(({ group_name, status }) => {
					initialGroups.push(group_name)
					status.forEach(({ err_code, err_type }) => {
						errorCodes.push(err_code)
						errorTypes.push(err_type)
					})
				})

				setState((prevState) => (
					{
						...prevState,
						alerts,
						initialAlerts: JSON.parse(JSON.stringify(alerts)),
						groups: _.uniq(initialGroups).sort(),
						statuses: _.union(_.uniq(errorTypes).sort(), _.uniq(errorCodes).sort())
					}
				))
			})
			.catch(err => {
				toggleLoader(false);
				console.log('!getHealthAlerts error: ', err.response, err)
				toastr.error(t('toastrUnableGetDevice'));
			})
	}

	const setCameraHealthAlertStatus = (stateArgs) => {
		const params = stateArgs
		toggleLoader(true);
		deviceAcknowledgeClear(params)
			.then(res => {
				toggleLoader(false)
				toastr.success('', i18n.t('toastMessage.toastrChangesSaved'))
				setState((prevState) => ({ ...prevState, showModalErrorStatus: false }))
				getCameraHealthAlerts()
			})
			.catch((error) => {
				toggleLoader(false)
				console.log('!updateDevice error: ', error.response, error)
				toastr.error(t('errUnableSaveDevice'));
			})
	}

	const setCameraHealthDeviceSilanced = (stateArgs) => {
		const params = stateArgs
		toggleLoader(true);
		deviceSilenceErrors(params)
			.then(res => {
				toggleLoader(false)
				toastr.success('', i18n.t('toastMessage.toastrChangesSaved'))
				setState((prevState) => ({ ...prevState, showModalSilencedErrors: false }))
				getCameraHealthAlerts()
			})
			.catch((error) => {
				toggleLoader(false)
				console.log('!updateDevice error: ', error.response, error)
				toastr.error(t('errUnableSaveSilence'));
			})
	}

	const filterColumn = (e, column, value) => {
		let newAlerts = [];
		if (column === 'clear_all') {
			if (!alerts.length) {
				setState((prevState) => ({ ...prevState, alerts: initialAlerts }))
			}
			else {
				setState((prevState) => ({ ...prevState, alerts: [] }))
			}
			return
		}

		if (!e.target.checked) {
			if (column === 'latest_hdderror') {
				value === 'Needs Reformat'
					? newAlerts = alerts.filter(e => _.isEmpty(e.latest_hdderror) || e.latest_hdderror.count === 0)
					: newAlerts = alerts.filter(e => !_.isEmpty(e.latest_hdderror) && e.latest_hdderror.count > 0)
			}
			else if (column === 'group_name') {
				newAlerts = alerts.filter(e => value === 'No group' ? (e.group_name && e.group_name.length) : e.group_name !== value);
			}
			else if (column === 'status') {
				if (['crit', 'warn'].includes(value)) {
					newAlerts = alerts.filter(e => {
						return !_.some(e.status, ['err_type', value])
					});
				}
				else {
					newAlerts = alerts.filter(e => {
						return !_.some(e.status, ['err_code', value])
					});
				}
			}
			else {
				newAlerts = alerts.filter(e => e[column] !== value);
			}
			e.target.checked = true;
		} else {
			if (column === 'latest_hdderror') {
				newAlerts = [...alerts];
				value === 'Needs Reformat'
					? newAlerts.unshift(...initialData.filter(e => !_.isEmpty(e.latest_hdderror) && e.latest_hdderror.count > 0))
					: newAlerts.unshift(...initialData.filter(e => _.isEmpty(e.latest_hdderror) || e.latest_hdderror.count === 0))
			}
			else if (column === 'group_name') {
				newAlerts = [...initialAlerts.filter(e => value === 'No group' ? (!e.group_name || !e.group_name.length) : e.group_name === value), ...alerts];
			}
			else if (column === 'status') {
				if (['crit', 'warn'].includes(value)) {
					newAlerts = [...initialAlerts.filter(e => _.some(e.status, ['err_type', value])), ...alerts]
				} else {
					newAlerts = [...initialAlerts.filter(e => _.some(e.status, ['err_code', value])), ...alerts]
				}
			} else {
				newAlerts = [...initialAlerts.filter(e => e[column] === value), ...alerts];
			}
			e.target.checked = false;
		}

		setState((prevState) => ({ ...prevState, alerts: newAlerts }))
	}

	const alertsSearch = (e) => {
		const value = e.target.value.toLowerCase()
		const filter = initialAlerts.filter(item => {
			return (
				(item.vehicle_name && item.vehicle_name.toLowerCase().includes(value))
			)
		})

		setState((prevState) => ({ ...prevState, alerts: filter, searchText: e.target.value }))
	}

	const onSilencedClick = (e, device) => {
		e.preventDefault()
		setState((prevState) => ({ ...prevState, selectedDevice: device, showModalSilencedErrors: true }))
	}

	const setAlertsSort = (sortName, sortOrder) => {
		setState((prevState) => ({ ...prevState, sortName, sortOrder }))
	}

	const isFilterStatusChecked = (status) => {
		if (['crit', 'warn'].includes(status)) {
			return alerts.filter(e => {
				return _.some(e.status, ['err_type', status])
			}).length
		}
		return alerts.filter(e => {
			return _.some(e.status, ['err_code', status])
		}).length
	}

	const cameraNameFormatter = (cell, row) => {
		const editVehicle = (e, id) => {
			e.preventDefault();
			navigate('/managevehicles/edit', {
				state: { id, company, type: 'from_camera_health' },
			});
		}

		return (
			<div className="camera-name-wrapper">
				{isWebfleetMode
					? <>{cell || t('unnamed')}</>
					: <CustomButton variant="link" onClick={(e) => editVehicle(e, row.device_id)}>{cell || t('unnamed')}</CustomButton>
				}
			</div>
		)
	}

	const statusFormatter = (cell, row) => {
		const res = []

		const warnErrors = _.groupBy(row.status.filter(({ err_type, is_ack }) => err_type === 'warn' && !is_ack), 'err_code')
		const critErrors = _.groupBy(row.status.filter(({ err_type, is_ack }) => err_type === 'crit' && !is_ack), 'err_code')
		const acknErrors = _.groupBy(row.status.filter(({ is_ack }) => is_ack), 'err_code')

		let groupCodes = Object.keys(warnErrors)
		for (let i = 0; i < groupCodes.length; i++) {
			const code = groupCodes[i]
			const errorsArr = warnErrors[code]
			res.push(
				<CustomButton
					key={`warn${code}`}
					variant="primary"
					style={{ marginRight: '5px' }}
					size="hs"
					prefix="warn"
					onClick={(e) => { setState((prevState) => ({ ...prevState, selectedAlert: { ...errorsArr[0], device_id: row.device_id }, showModalErrorStatus: true })) }}
				>
					{code}
					<span className="count">{errorsArr[0]?.count || errorsArr.length}</span>
				</CustomButton>
			)
		}

		groupCodes = Object.keys(critErrors)
		for (let i = 0; i < groupCodes.length; i++) {
			const code = groupCodes[i]
			const errorsArr = critErrors[code]
			res.push(
				<CustomButton
					key={`crit${code}`}
					variant="primary"
					style={{ marginRight: '5px' }}
					size="hs"
					prefix="crit"
					onClick={(e) => { setState((prevState) => ({ ...prevState, selectedAlert: { ...errorsArr[0], device_id: row.device_id }, showModalErrorStatus: true })) }}
				>
					{code}
					<span className="count">{errorsArr[0]?.count || errorsArr.length}</span>
				</CustomButton>
			)
		}

		groupCodes = Object.keys(acknErrors)
		for (let i = 0; i < groupCodes.length; i++) {
			const code = groupCodes[i]
			const errorsArr = acknErrors[code]
			res.push(
				<CustomButton
					key={`ackn${code}`}
					variant="primary"
					style={{ marginRight: '5px' }}
					size="hs"
					prefix="ackn"
					onClick={(e) => { setState((prevState) => ({ ...prevState, selectedAlert: { ...errorsArr[0], device_id: row.device_id }, showModalErrorStatus: true })) }}
				>
					{code}
					<span className="count">{errorsArr[0]?.count || errorsArr.length}</span>
				</CustomButton>
			)
		}

		return <>{res}</>
	}

	const viewFormatter = (cell, row) => {
		return <Link to="/" onClick={(e) => onSilencedClick(e, row)}>{t('linkView')}</Link>
	}

	return (
		<div className="camera-health">
			{!isWebfleetMode &&
				<div className="page-subheader">
					<div className="subheader-section search-flex">
						<SearchBar term={searchText} onSearch={alertsSearch} placeholder="Search Vehicles..." />
					</div>
				</div>
			}

			<div className="description-wrapper">
				<div className="description left-column">
					<p>{t('descriptionLeftColumn_1')}</p>
					<p>{t('descriptionLeftColumn_2')}</p>
					<p>{t('descriptionLeftColumn_3')}</p>
				</div>
				<div className={`description right-column${!isInternalUser(userRole) ? ' customer' : ''}`}>
					<div className="status-definitions-wrapper">
						<div className="title">{t('titleStatusDefinitions')}</div>
						<div className="content">
							<div className="mb-10">{t('textLearnMore')}</div>
							<div className="status-definitions">
								{[
									{ err_code: 'WARN', type: 'warn', title: t('statusTitleWarningError'), body: t('statusBodyWarningError') },
									{ err_code: 'CRITICAL', type: 'crit', title: t('statusTitleCriticalError'), body: t('statusBodyCriticalError') },
									{ err_code: 'ACKN', type: 'ackn', title: t('statusTitleAcknowledgedError'), body: t('statusBodyAcknowledgedError') },
								].map((item) => {
									const { err_code, type, is_ack, title } = item

									if (!isInternalUser(userRole) && type === 'crit') {
										return
									}

									return (
										<CustomOverlayTrigger
											key={title}
											{...item}
											placement="top"
											trigger="click"
											closeBtn={true}
											prefix={type}
										>
											<div>
												<CustomButton
													variant="primary"
													size="hs"
													prefix={type}
												>
													{err_code}
												</CustomButton>
											</div>
										</CustomOverlayTrigger>
									)
								})}
							</div>
						</div>
					</div>
				</div>
			</div>

			<main className='main-content-block pt-0'>
				<BootstrapTable
					data={alerts}
					{...tableAdjustableColOptions}
					options={{
						...tableAdjustableColOptions.options,
						sortName,
						sortOrder,
						onSortChange: setAlertsSort,
					}}
				>
					<TableHeaderColumn
						width="15%"
						dataField="vehicle_name"
						dataFormat={cameraNameFormatter}
						className="with-filter"
					>
						<div className="filter-wrapper">
							<label className="filter-label">{t('labelCameraName')}</label>
							<span className="order" style={{ cursor: 'pointer', marginLeft: '4px' }}>
								{(sortName !== 'vehicle_name' || (sortName === 'vehicle_name' && sortOrder === 'desc')) &&
									<span className="dropdown" onClick={() => setAlertsSort('vehicle_name', 'asc')}><span className="caret"></span></span>
								}
								{(sortName !== 'vehicle_name' || (sortName === 'vehicle_name' && sortOrder === 'asc')) &&
									<span className="dropup" onClick={() => setAlertsSort('vehicle_name', 'desc')}><span className="caret"></span></span>
								}
							</span>
							<Dropdown drop id="type-filter-btn" className="filter-dropdown">
								<Dropdown.Toggle as='span' className="typeFilter" bsPrefix="no-caret">
									{filterIcon}
								</Dropdown.Toggle>
								<Dropdown.Menu className="typeFilterMenu">
									<div className="groups-header">
										<h5 className="category-title">{t('titleVehicles')}</h5>
									</div>
									{initialAlerts.map(({ device_id, vehicle_name }) => {
										return (
											<li
												key={device_id}
												title={vehicle_name}
											>
												<input
													type="checkbox"
													checked={alerts.filter(e => e.vehicle_name === vehicle_name).length}
													onChange={(e) => filterColumn(e, 'vehicle_name', vehicle_name)}
												/>
												<span className="groupName">{vehicle_name}</span>
											</li>
										)
									})}
									<div className="clear-all-container">
										<CustomButton variant="primary-outline" size="sm" prefix="clear-all" onClick={(e) => filterColumn(e, 'clear_all')}>
											{`${alerts.length === 0 ? t('select') : t('clear')} ${t('all')}`}
										</CustomButton>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>
					</TableHeaderColumn>
					<TableHeaderColumn
						width="15%"
						dataField="group_name"
						dataFormat={formatGroup}
						className="with-filter"
					>
						<div className="filter-wrapper">
							<label className="filter-label">{t('labelGroupName')}</label>
							<span className="order" style={{ cursor: 'pointer', marginLeft: '4px' }}>
								{(sortName !== 'group_name' || (sortName === 'group_name' && sortOrder === 'desc')) &&
									<span className="dropdown" onClick={() => setAlertsSort('group_name', 'asc')}><span className="caret"></span></span>
								}
								{(sortName !== 'group_name' || (sortName === 'group_name' && sortOrder === 'asc')) &&
									<span className="dropup" onClick={() => setAlertsSort('group_name', 'desc')}><span className="caret"></span></span>
								}
							</span>
							<Dropdown drop id="type-filter-btn" className="filter-dropdown">
								<Dropdown.Toggle as='span' className="typeFilter" bsPrefix="no-caret">
									{filterIcon}
								</Dropdown.Toggle>
								<Dropdown.Menu className="typeFilterMenu">
									<div className="groups-header">
										<h5 className="category-title">{t('titleGroups')}</h5>
									</div>
									{groups.map(groupItem => {
										return (
											<li
												key={groupItem}
												title={groupItem}
											>
												<input
													type="checkbox"
													checked={groupItem === 'No group' ? alerts.filter(e => !e.group_name).length : alerts.filter(e => e.group_name === groupItem).length}
													onChange={(e) => filterColumn(e, 'group_name', groupItem)}
												/>
												<span className="groupName">{groupItem}</span>
											</li>
										)
									})}
									<div className="clear-all-container">
										<CustomButton variant="primary-outline" size="sm" prefix="clear-all" onClick={(e) => filterColumn(e, 'clear_all')}>
											{`${alerts.length === 0 ? t('select') : t('clear')} ${t('all')}`}
										</CustomButton>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>
					</TableHeaderColumn>
					<TableHeaderColumn
						width="60%"
						dataField="status"
						dataFormat={statusFormatter}
						className="with-filter"
					>
						<div className="filter-wrapper">
							<label className="filter-label">{t('labelStatus')}</label>
							<Dropdown drop id="type-filter-btn" className="filter-dropdown">
								<Dropdown.Toggle as='span' className="typeFilter" bsPrefix="no-caret">
									{filterIcon}
								</Dropdown.Toggle>
								<Dropdown.Menu className="typeFilterMenu">
									<div className="groups-header">
										<h5 className="category-title">{t('titleErrors')}</h5>
									</div>
									{statuses.map((status) => {
										let name = status
										switch (status) {
											case 'crit':
												name = t('nameCritical')
												break
											case 'warn':
												name = t('nameWarning')
												break
										}

										return (
											<li
												key={status}
												title={status}
											>
												<input
													type="checkbox"
													checked={isFilterStatusChecked(status)}
													onChange={(e) => filterColumn(e, 'status', status)}
												/>
												<span className="groupName">{name}</span>
											</li>
										)
									})}
									<div className="clear-all-container">
										<CustomButton variant="primary-outline" size="sm" prefix="clear-all" onClick={(e) => filterColumn(e, 'clear_all')}>
											{`${alerts.length === 0 ? t('select') : t('clear')} ${t('all')}`}
										</CustomButton>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="device_id"
						dataFormat={viewFormatter}
						isKey
					>
					{t('labelSilencedErrors')}
					</TableHeaderColumn>
				</BootstrapTable>
			</main>

			{showModalErrorStatus && (
				<ModalErrorStatus
					selectedAlert={selectedAlert}
					setCameraHealthAlert={setCameraHealthAlertStatus}
					onHide={() => setState((prevState) => ({ ...prevState, showModalErrorStatus: false }))}
				/>
			)}

			{showModalSilencedErrors && (
				<ModalSilencedErrors
					selectedDevice={selectedDevice}
					setCameraHealthAlert={setCameraHealthDeviceSilanced}
					isInternalUser={isInternalUser(userRole)}
					onHide={() => setState((prevState) => ({ ...prevState, showModalSilencedErrors: false }))}
				/>
			)}
		</div>
	)
}

DeviceHealth.propTypes = {
	company: PropTypes.objectOf(PropTypes.any).isRequired,
}

const mapStateToProps = ({ user, company, webfleetData }) => ({
	user: user.user,
	company: company.company,
	webfleetDeviceId: webfleetData.deviceId,
})

const mapDispatchToProps = dispatch => ({
	toggleLoader: (show) => {
		dispatch({ type: 'TOGGLE_LOADER', payload: show });
	}
})

export default connect(mapStateToProps, mapDispatchToProps)(DeviceHealth);
